import { type RefObject, useCallback, useEffect, useRef } from "react";

type FocusFallback = HTMLElement | RefObject<HTMLElement | null> | null;
type CancelScheduledVerification = () => void;
const FOCUS_VERIFICATION_TIMEOUT_MS = 2000;

const isHTMLElement = (value: unknown): value is HTMLElement => {
  return typeof HTMLElement !== "undefined" && value instanceof HTMLElement;
};

const resolveFallback = (fallback?: FocusFallback): HTMLElement | null => {
  if (!fallback) return null;
  if (isHTMLElement(fallback)) return fallback;

  return fallback.current;
};

const isFocusable = (element: HTMLElement): boolean => {
  return (
    element.hasAttribute("tabindex") ||
    element.matches(
      [
        "a[href]",
        "area[href]",
        "button:not([disabled])",
        "input:not([disabled])",
        "select:not([disabled])",
        "textarea:not([disabled])",
        "iframe",
        "summary",
        "[contenteditable='true']",
        "[contenteditable='']",
      ].join(","),
    )
  );
};

const focusElement = (
  element: HTMLElement,
  ensureFallbackFocusable: boolean,
): void => {
  const needsTemporaryTabIndex =
    ensureFallbackFocusable && !isFocusable(element);

  if (needsTemporaryTabIndex) {
    element.tabIndex = -1;
  }

  const removeTemporaryTabIndex = () => {
    if (element.getAttribute("tabindex") === "-1") {
      element.removeAttribute("tabindex");
    }
  };

  if (needsTemporaryTabIndex) {
    element.addEventListener("blur", removeTemporaryTabIndex, { once: true });
  }

  element.focus();

  if (needsTemporaryTabIndex && document.activeElement !== element) {
    element.removeEventListener("blur", removeTemporaryTabIndex);
    removeTemporaryTabIndex();
  }
};

const focusFallback = (fallback?: FocusFallback): void => {
  const fallbackElement = resolveFallback(fallback);

  if (!fallbackElement?.isConnected) return;

  focusElement(fallbackElement, true);
};

const isUnavailableForFocus = (element: HTMLElement): boolean => {
  if (
    element.matches(":disabled, [hidden]") ||
    element.closest('[inert], [aria-hidden="true"]')
  ) {
    return true;
  }

  if (typeof window === "undefined") return false;

  const style = window.getComputedStyle(element);
  return style.display === "none" || style.visibility === "hidden";
};

const documentFocusWasLost = (capturedElement: HTMLElement): boolean => {
  if (typeof document === "undefined") return false;

  const activeElement = document.activeElement;
  return (
    activeElement === null ||
    activeElement === document.body ||
    activeElement === document.documentElement ||
    activeElement === capturedElement
  );
};

const monitorPostCommitFocus = (
  capturedElement: HTMLElement,
  onFocusLost: () => void,
): CancelScheduledVerification => {
  if (typeof document === "undefined") {
    return () => {};
  }

  let observer: MutationObserver | null = null;
  let verificationFrame: number | null = null;
  let verificationTimeout: ReturnType<typeof setTimeout> | null = null;
  let cancelled = false;

  const cancel = () => {
    if (cancelled) return;
    cancelled = true;
    observer?.disconnect();
    observer = null;

    if (verificationFrame !== null && typeof window !== "undefined") {
      window.cancelAnimationFrame(verificationFrame);
      verificationFrame = null;
    }

    if (verificationTimeout !== null) {
      clearTimeout(verificationTimeout);
      verificationTimeout = null;
    }
  };

  const verify = () => {
    if (cancelled) return;

    const capturedElementIsConnected = capturedElement.isConnected;
    if (
      capturedElementIsConnected &&
      document.activeElement === capturedElement
    ) {
      return;
    }

    const shouldRestoreFallback =
      documentFocusWasLost(capturedElement) &&
      (!capturedElementIsConnected || isUnavailableForFocus(capturedElement));

    cancel();
    if (shouldRestoreFallback) {
      onFocusLost();
    }
  };

  if (typeof MutationObserver !== "undefined" && document.documentElement) {
    observer = new MutationObserver(verify);
    observer.observe(document.documentElement, {
      attributes: true,
      childList: true,
      subtree: true,
    });
  }

  if (
    typeof window !== "undefined" &&
    typeof window.requestAnimationFrame === "function"
  ) {
    verificationFrame = window.requestAnimationFrame(() => {
      verificationFrame = null;
      verify();
    });
  }

  verificationTimeout = setTimeout(cancel, FOCUS_VERIFICATION_TIMEOUT_MS);
  return cancel;
};

/**
 * useFocusRetention
 *
 * Captures the intended focus target before a mutation that may re-render or
 * remove it, then restores focus even when called before React commits the
 * resulting DOM update. Restore first uses the captured element when it is still
 * connected, then watches the bounded post-commit window and moves focus to the
 * latest connected fallback if that element is removed or made unavailable.
 * Ref fallbacks are resolved when they are focused; non-focusable fallback
 * containers receive a temporary `tabIndex = -1` for programmatic focus without
 * changing their sequential keyboard behavior.
 */
export const useFocusRetention = (): {
  capture: (target?: HTMLElement | null) => void;
  restore: (fallback?: FocusFallback) => void;
} => {
  const capturedElementRef = useRef<HTMLElement | null>(null);
  const cancelVerificationRef = useRef<CancelScheduledVerification | null>(
    null,
  );

  const cancelPendingVerification = useCallback(() => {
    cancelVerificationRef.current?.();
    cancelVerificationRef.current = null;
  }, []);

  const capture = useCallback(
    (target?: HTMLElement | null) => {
      cancelPendingVerification();

      if (target !== undefined) {
        capturedElementRef.current = target;
        return;
      }

      if (typeof document === "undefined") {
        capturedElementRef.current = null;
        return;
      }

      capturedElementRef.current = isHTMLElement(document.activeElement)
        ? document.activeElement
        : null;
    },
    [cancelPendingVerification],
  );

  const restore = useCallback(
    (fallback?: FocusFallback) => {
      cancelPendingVerification();

      const capturedElement = capturedElementRef.current;
      const shouldUseCapturedElement = capturedElement?.isConnected === true;

      capturedElementRef.current = null;

      if (!shouldUseCapturedElement) {
        focusFallback(fallback);
        return;
      }

      focusElement(capturedElement, false);

      if (document.activeElement !== capturedElement) {
        focusFallback(fallback);
        return;
      }

      cancelVerificationRef.current = monitorPostCommitFocus(
        capturedElement,
        () => {
          cancelVerificationRef.current = null;

          focusFallback(fallback);
        },
      );
    },
    [cancelPendingVerification],
  );

  useEffect(() => {
    return cancelPendingVerification;
  }, [cancelPendingVerification]);

  return { capture, restore };
};
